import { buildCache } from '../utils/cache';
import { ISummary, TAIForm } from 'src/types/aiForm';
import { IOrgUpdate } from 'src/types/org';
import { Storage, API } from 'aws-amplify';
import { IndexedHeap } from 'src/types/heap';
import { processResponse } from '../utils/api';

const indexCache = buildCache<IFileIndex>('indexCache');
const historyCache = buildCache<IHistory>('historyCache');
const monthCache = buildCache<IFileIndex>('monthCache');

const RecentRequest = (a: IRequest, b: IRequest) => (b.index.time) - (a.index.time);
const RecentSummary = (a: ISummary, b: ISummary) => (b.updateDate) - (a.updateDate);
let loaded = false;

const startYear = 2018;

const getKey = (a: ISummary): string => {
  return `${a.userId}-${a.categoryId}`;
};
let latestRequests = new IndexedHeap<IRequest>(RecentRequest, (a: IRequest) => a.index.key);
let latestSummaries = new IndexedHeap<ISummary>(RecentSummary, (a: ISummary) => getKey(a));

export const persist = () => {
  const serialized = JSON.stringify(latestRequests.take(500));
  localStorage.setItem('recentRecords', serialized);
};

export const read = () => {
  const records: IRequest[] = JSON.parse(localStorage.getItem('recentRecords') || '[]');
  for (const record of records) {
    indexCache.set(record.index.key, record.index);
    latestRequests.insert(record);
  }

  loaded = true;
};

export const flush = () => {
  indexCache.clear();
  historyCache.clear();
  monthCache.clear();
  latestRequests = new IndexedHeap<IRequest>(RecentRequest, (a: IRequest) => a.index.key);
  latestSummaries = new IndexedHeap<ISummary>(RecentSummary, (a: ISummary) => getKey(a));
  localStorage.setItem('recentRecords', JSON.stringify([]));
};

interface IHistory {
  _previous: IHistory | null;
  index: IFileIndex | null;
  indexUpdate: IFileIndex;
  reason: string;
  record: IRequest | null;
  recordUpdate: IRequest | null;
}

export interface IFileIndex {
  key: string;
  year: number;
  month: number;
  userId: string;
  categoryId: string,
  time: number,
  submittedTime: number,
  file: string,
  size: number;
};

export interface IRequest {
  index: IFileIndex;
  file: TAIForm;
  key: string;
}

interface IMonthKey {
  year: number;
  month: number;
}



/**
 * Always add the current month to the fetch list, to maintain a stream of updates.
 * Using the disk, read out the already cached requests that have been stashed previously.
 * Use the earliest record on file to determine the starting month for retrieving historical
 * records.
 * @returns months IMonthKey[]
 */
export const buildMonths = () => {
  const now = new Date();

  if (!loaded)
    read();

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Add the current month always, we want to keep
  // scanning this month for changes
  const months: IMonthKey[] = [];
  months.push({ year: currentYear, month: currentMonth });

  // Remove the current month since it's already added, Make sure if the month is
  // January that removing 1 will move us to december of the year before
  let year = currentYear;
  let month = currentMonth - 1;
  if (month < 0) {
    year = year - 1;
    month = 12 + month;
  }

  // Check if we have the sorted list in the storage, if so, grab the earliest date
  // use that month as the starting point.
  const earliest: IRequest | null = latestRequests.last();
  if (earliest) {
    const datetime = new Date(earliest.index.time);
    year = datetime.getFullYear();
    month = datetime.getMonth();
  }

  const diff = (year - startYear) * 12;
  const monthsAgo = month + diff + 1;

  let tempMonth = month;
  let tempYear = year;
  for (let x = 0; x < monthsAgo; x++) {
    months.push({ year: tempYear, month: tempMonth });
    // Move to the previous month
    tempMonth = tempMonth - 1;
    if (tempMonth < 0) {
      tempYear = tempYear - 1;
      tempMonth = 12 + tempMonth;
    }
  }

  return months;
};

/**
 * 
 * @param year 
 * @param month 
 * @returns 
 */
export const fetchMonth = async (year: number, month: number): Promise<IFileIndex[]> => {
  const monthCache = buildCache<IFileIndex[]>('monthCache');
  const now = new Date();
  // Always re-pull current month
  if (now.getFullYear() + '/' + now.getMonth() !== year + '/' + month) {
    const monthFiles = monthCache.get(year + '/' + month);
    if (monthFiles) {
      return Promise.resolve(monthFiles);
    }
  }

  // If we cannot serve from the cache, go to the server.
  const files = await Storage.list(year + '/' + month, { maxKeys: 'ALL' });
  const mapped = files.map((item) => parseIndex(item));
  monthCache.set(year + '/' + month, mapped);
  return mapped;
};

export const parseIndex = (index: any): IFileIndex => {
  let parts = index.key.split('/');
  const month = parts[1];
  const year = parts[0];
  const userId = parts[2];
  const categoryId = parts[3];
  const key = `${userId}-${categoryId}`;
  const updateTime = parseInt(parts[parts.length - 1], 10);

  return {
    key: key,
    year: year,
    month: month,
    userId: userId,
    categoryId: categoryId,
    time: updateTime,
    submittedTime: updateTime,
    file: index.key,
    size: index.size,
  };
};


/**
 * Take an array of generic items <T> and use the batch size to break up the records into
 * as many batches as needed based on the batch size.
 * @param fetches 
 * @param batchSize 
 * @returns 
 */
export const buildBatches = <T>(fetches: T[], batchSize: number = 100): T[][] => {
  let batches: T[][] = [];
  let batch: T[] = [];
  let count: number = 1;
  for (let fetch of fetches) {
    batch.push(fetch);
    const isBatchFilled = batch.length > 0 && (batch.length % batchSize) === 0;
    const isEnd = count >= fetches.length;
    if (isBatchFilled || isEnd) {
      batches.push(batch);
      batch = [];
    }
    count++;
  }
  return batches;
};


// 
/**
 * Distil the list records down to unique records and the most recent update.  Checking to see
 * if the record has already entered our memory cache.  If it doesn't exist in the caches or has
 * a more recent update timestamp, we will need to add to the batch of records to be retrieved.
 * @param fileIndex 
 * @returns 
 */

interface IFetchIndex {
  [key: string]: IFileIndex;
}
export const buildFetches = (fileIndex: IFileIndex[]) => {

  // Setup batches for retrieving file contents
  // If the index record is not in the cache, it's been updated more recently, or we haven't retrieved
  // the content of the file, all circumstances requiring pulling the file contents.
  const fetchIndex: IFetchIndex = {};
  for (let index of fileIndex) {
    if (index.userId && index.categoryId) {
      let item = indexCache.get(index.key);

      let updated = false;
      let exists = item ? true : false;
      let cached = latestRequests.find(index.key);
      let currentUpdate = item ? item.time : null;

      if (item) updated = item.time < index.time;
      else item = index;

      // If we have a record matching the unique key, check if the submit time is earlier meaning
      // we can get closer to the first submission if we inherit that time.
      if (updated) {
        item.time = index.time;
        item.size = index.size;
        item.file = index.file;

        if (item.time == null)
          console.log(item, index);
      }
      item.submittedTime = item.submittedTime > index.submittedTime ? index.submittedTime : item.submittedTime;

      if (!exists || updated || !cached) {
        const reason = !exists ? 'Missing Index Record' : (updated ? `Updated from ${currentUpdate} to ${index.time}` : 'Missing Request Record');

        const recordReport: IHistory = {
          _previous: historyCache.get(index.key),
          index: exists ? item : null,
          indexUpdate: index,
          reason: reason,
          record: cached,
          recordUpdate: null
        };
        historyCache.set(index.key, recordReport);
        indexCache.set(item.key, item);
        fetchIndex[item.key] = item;
      }
    }
  }
  const fetches: IFileIndex[] = [];
  for (const key of Object.keys(fetchIndex))
    fetches.push(fetchIndex[key]);
  return fetches;
};



/**
 * Retrieve all the partial form request records from the API from the list 
 * of necessary IFileIndex records.
 * @param fetches 
 * @returns 
 */
export const fetchRequests = async (fetches: IFileIndex[]): Promise<IRequest[]> => {
  const requests = fetches.map((index) => findRequest(index));
  return await Promise.all(requests);
};

export const findRequest = async (index: IFileIndex): Promise<IRequest> => {
  const data = await API.post('AiFormRequest', '/save?key=get', {
    body: { userId: index.userId, categoryId: index.categoryId },
  });
  const file: IRequest = { file: data[0], index: index, key: index.key };
  return file;
};


interface ILoadOptions {
  onProgressUpdate?(batch: string, records: IRequest[]): void;
}

export const fetchSummaries = async (fromTime?: number, toTime?: number): Promise<ISummary[]> => {
  const start = fromTime || 0;
  const end = toTime || new Date().getTime();

  return await API.post('AiFormRequest', '/save?key=fetchSummaries', {
    body: {
      fromTime: start,
      toTime: end
    }
  });
};



/**
 * retrieve summaries in a smart way.  using the IndexedHeap as a cache, look
 * back at the earliest and the most recent record.  
 * 
 * Move forward from most recent and break into calendar month date ranges til we reach the present
 * Move backward from oldest and break into calendar month date ranges til we reach app start date (2018-01-01)
 * @param options 
 * @returns 
 */
interface IDateRange {
  start: Date;
  end: Date;
}

export const loadSummaries = async (options: ILoadOptions) => {
  const dates = generateDates();
  const batches = buildBatches<IDateRange>(dates);
  for (const batch of batches) {
    const results = await Promise.all(batch.map((range) => fetchSummaries(range.start.getTime(), range.end.getTime())));
    for (let b = 0; b < batch.length; b++) {
      const data = results[b];
      for (const summary of data)
        latestSummaries.insert(summary);
    }
  }
  return latestSummaries;
};

export const getFile = async (filePath: string) => {
  return await API.post('AiFormRequest', '/save?key=getFile', {
    body: {
      filePath: filePath
    }
  }).then((response) => processResponse(response));
};

export interface IStatusUpdate {
  user: string;
  status: string;
  time: number;
  comment?: string;
  aiCode?: string;
  aiLevelInd?: string;
  appealEligible?: boolean;
  requireTestingPlan?: boolean;
};


export interface IStatusUpdateResponse {
  userId: string;
  categoryId: string;
  update: IStatusUpdate;
}

/**
 * 
 * @param userId 
 * @param categoryId 
 * @param updates 
 * @returns record : DynamoRecord
 */
export const statusUpdate = async (userId: string, categoryId: string, updates: Partial<IStatusUpdate>): Promise<IStatusUpdate> => {
  const response: IStatusUpdateResponse = await API.post('AiFormRequest', '/save?key=statusUpdate', {
    body: {
      userId: userId,
      categoryId: categoryId,
      update: updates
    }
  }).then((response) => processResponse(response));
  return response.update;
};


export interface INote {
  user: string;
  message: string;
  time: number;
};

export interface INoteResponse {
  userId: string;
  categoryId: string;
  note: INote;
}

/**
 * 
 * @param userId 
 * @param categoryId 
 * @param note 
 * @returns record : DynamoRecord
 */
export const noteAdd = async (userId: string, categoryId: string, note: Partial<INote>): Promise<INote> => {
  const response: INoteResponse = await API.post('AiFormRequest', '/save?key=noteAdd', {
    body: {
      userId: userId,
      categoryId: categoryId,
      note: note
    }
  }).then((response) => processResponse(response));
  return response.note;
};

export const generateDates = () => {
  const max = new Date();
  const min = new Date(2018, 0, 1);
  const newest = latestSummaries.first();
  const start = newest ? new Date(newest.updateDate) : min;
  let current = start;
  const months: IDateRange[] = [];
  while (current < max) {
    const next = new Date(current);
    next.setDate(next.getDate() + 7);
    const end = next < max ? next : max;
    const range: IDateRange = {
      start: current,
      end: end
    };
    months.push(range);
    current = end;
  }
  return months;
};



export const fetchAll = async (options: ILoadOptions) => {
  const now = new Date();
  // Compile a list of missing month index listings that haven't been retrieved
  // which always includes current month to grab new updates, only pull at most
  // 12 months at a time.
  const months = buildMonths();
  const batches = buildBatches<IMonthKey>(months, 12);

  let status = 'Compiling Requests';
  if (options.onProgressUpdate)
    options.onProgressUpdate(status, []);

  for (const batch of batches) {
    const monthKey = batch[batch.length - 1];
    status = "Retrieving Updates through " + monthKey.year + '/' + monthKey.month;
    if (options.onProgressUpdate)
      options.onProgressUpdate(status, []);

    const monthResults = await Promise.all(batch.map((month) => fetchMonth(month.year, month.month)));
    const possibleFetches = monthResults.flat();
    const fetches: IFileIndex[] = buildFetches(possibleFetches);

    // Retrieve the Historical Record Contents for the months above
    status = now.getFullYear() + '/' + now.getMonth();
    if (options.onProgressUpdate)
      options.onProgressUpdate(status, latestRequests.all());

    const requestBatches = buildBatches<IFileIndex>(fetches);
    for (const requestBatch of requestBatches) {
      const requests = requestBatch.map((index) => findRequest(index));
      const fetchResults: IRequest[] = await Promise.all(requests);
      for (let f = 0; f < fetchResults.length; f++) {
        const index = requestBatch[f];
        const result = fetchResults[f];
        if (result) {
          latestRequests.insert(result);
          const history = historyCache.get(index.key);
          if (history)
            history.recordUpdate = result;
        }
        const oldestDate = new Date(index.time);
        status = oldestDate.getFullYear() + '/' + oldestDate.getMonth();
      }
      persist();
      if (options.onProgressUpdate)
        options.onProgressUpdate(status, latestRequests.all());
    }
  }

  const retrieved = latestRequests.all();
  persist();
  return retrieved;
};
